/* eslint-disable import/prefer-default-export */

import dayjs from 'dayjs';

import { UserVehicleTracker } from './@types';

const isDateFormatMatch = (date1: string, date2: string, format: string) => (
  dayjs(date1).format(format) === dayjs(date2).format(format)
);

const formatCreatedAtDate = (valuations: UserVehicleTracker['trackerValuations']) =>
  valuations?.map((valuation) => (
    valuation.isPredicted
      ? { ...valuation, createdAt: dayjs(valuation?.predictedDate).format('YYYY-MM-DD') || '' }
      : { ...valuation, createdAt: dayjs(valuation.createdAt).format('YYYY-MM-DD') }
  ));

const filterValuationsByTimeRange = (valuations: UserVehicleTracker['trackerValuations'], timeRange: number) => {
  if (timeRange === 0) {
    return valuations;
  }

  const startingPeriod = dayjs(valuations[valuations.length - 1]?.createdAt).subtract(timeRange - 1, 'month').startOf('month');

  const valuationsWithinTimeRange = valuations.filter((valuation) => {
    const isAfterStartingPeriod = dayjs(valuation.createdAt).isSameOrAfter(dayjs(startingPeriod), 'month');
    return isAfterStartingPeriod ? valuation : undefined;
  });

  // We always want to start the chart on the 1st of the month, so we steal a valuation from the previous month
  // so that we can plot a value for the 1st of the month if one does not exist.
  const firstValuationBeforeTimeRange = valuations.filter((valuation) => (dayjs(valuation.createdAt).isBefore(dayjs(startingPeriod), 'month') ? valuation : undefined)).slice(-1)[0];
  if (firstValuationBeforeTimeRange) {
    firstValuationBeforeTimeRange.createdAt = dayjs(startingPeriod).format('YYYY-MM-DD');
    firstValuationBeforeTimeRange.isDisabledOnChart = true;
    valuationsWithinTimeRange.unshift(firstValuationBeforeTimeRange);
  }
  return valuationsWithinTimeRange;
};

const filterOneValuationPerDay = (valuations: UserVehicleTracker['trackerValuations']) =>
  valuations?.filter((valuation, index) => {
    const isLastValuation = index === valuations.length - 1;
    if (!isLastValuation) {
      return !isDateFormatMatch(valuation.createdAt, valuations[index + 1]?.createdAt, 'YYYY-MM-DD') ? valuation : undefined;
    }
    return valuation;
  });

/**
 * We want to get rid of predicted valuations when real valuations exist for that month.
 * The only exception is for the first month, we want to keep a prediction so data exists at the
 * start of the chart (no blank spaces).
 */
const filterUnwantedPredictions = (valuations: UserVehicleTracker['trackerValuations']) =>
  valuations.filter((valuation, index) => {
    const isFirstValuation = index === 0;
    const isLastValuation = index === valuations.length - 1;
    if (valuation.isPredicted && !isFirstValuation && !isLastValuation) {
      return !isDateFormatMatch(valuation.createdAt, valuations[index + 1]?.createdAt, 'YYYY-MM') ? valuation : undefined;
    }
    return valuation;
  });

export const filterValuations = (valuations: UserVehicleTracker['trackerValuations'], timeRange = 12) => {
  const valuationsWithPredictedDates = formatCreatedAtDate(valuations);
  const valuationsWithinRange = filterValuationsByTimeRange(valuationsWithPredictedDates, timeRange);
  const valuationsWithoutUnwantedPredications = filterUnwantedPredictions(valuationsWithinRange);
  return filterOneValuationPerDay(valuationsWithoutUnwantedPredications);
};
